import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "../../lib/api";
import { ChevronRight, Calendar, User, Tag, Clock, CheckCircle2, ArrowRight, MapPin } from "lucide-react";
import CTASection from "../../components/public/CTASection";

const CATS = { actualites: "Actualités", conseils: "Conseils", "etudes-de-cas": "Études de cas" };

export function BlogListPage() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");
  useEffect(() => { api.get("/blog").then(r => setItems(r.data)); }, []);
  const filtered = filter === "all" ? items : items.filter(b => b.category === filter);
  const cats = Array.from(new Set(items.map(i => i.category)));

  return (
    <>
      <title>Blog — Conseils plomberie & climatisation | Kloub'Air Plomberie</title>
      <meta name="description" content="Tous nos conseils sur la climatisation, la plomberie, le chauffage. Guides et actualités par Kloub'Air Plomberie."/>
      <section className="pt-14 pb-10 bg-[color:var(--brand-navy)] text-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <nav className="text-xs text-white/60 mb-4 flex items-center gap-2"><Link to="/">Accueil</Link><ChevronRight className="w-3 h-3"/> Blog</nav>
          <h1 className="font-display text-4xl md:text-6xl font-black">Le blog</h1>
          <p className="mt-4 text-white/80 max-w-2xl">Conseils, actualités et études de cas sur la plomberie et la climatisation.</p>
        </div>
      </section>
      <section className="py-14">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex flex-wrap gap-2 mb-10">
            <button onClick={()=>setFilter("all")} className={`px-4 py-2 rounded-full text-xs font-bold uppercase tracking-wider ${filter==="all"?"bg-[color:var(--brand-navy)] text-white":"bg-[hsl(var(--muted))]"}`}>Tous</button>
            {cats.map(c => (
              <button key={c} onClick={()=>setFilter(c)} className={`px-4 py-2 rounded-full text-xs font-bold uppercase tracking-wider ${filter===c?"bg-[color:var(--brand-navy)] text-white":"bg-[hsl(var(--muted))]"}`}>
                {CATS[c] || c}
              </button>
            ))}
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map(p => (
              <Link key={p.id} to={`/blog/${p.slug}`} className="group rounded-3xl overflow-hidden bg-white border border-[hsl(var(--border))] shadow-hover" data-testid={`blog-card-${p.slug}`}>
                {p.cover_image && <div className="h-52 overflow-hidden"><img src={p.cover_image} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform"/></div>}
                <div className="p-6">
                  <div className="text-[11px] font-bold uppercase tracking-wider text-[color:var(--brand-sky)] mb-2">{CATS[p.category] || p.category}</div>
                  <h3 className="font-display text-xl font-bold text-[color:var(--brand-navy)] mb-2 line-clamp-2">{p.title}</h3>
                  <p className="text-sm text-[hsl(var(--muted-foreground))] line-clamp-3">{p.excerpt}</p>
                  <div className="mt-4 pt-4 border-t flex items-center gap-3 text-xs text-[hsl(var(--muted-foreground))]">
                    <span className="flex items-center gap-1"><User className="w-3 h-3"/> {p.author}</span>
                    <span className="flex items-center gap-1"><Calendar className="w-3 h-3"/> {new Date(p.created_at).toLocaleDateString("fr-FR")}</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
      <CTASection/>
    </>
  );
}

export function BlogDetailPage() {
  const { slug } = useParams();
  const [p, setP] = useState(null);
  useEffect(() => { api.get(`/blog/${slug}`).then(r => setP(r.data)).catch(() => setP(false)); }, [slug]);
  if (p === false) return <div className="py-24 text-center">Article introuvable.</div>;
  if (!p) return <div className="py-24 text-center">Chargement...</div>;

  // Supports both HTML (new TipTap editor) and legacy lightweight markdown.
  const isHtmlContent = /^\s*<(p|h[1-6]|ul|ol|blockquote|div|img|figure|iframe|strong|em)[\s>]/i.test(p.content || "");

  // Lightweight markdown: ##, ###, **bold**, - bullets, paragraphs
  const renderMarkdown = (md) => {
    const lines = (md || "").split("\n");
    const out = []; let list = [];
    const flush = () => { if (list.length) { out.push(<ul key={`u-${out.length}`} className="list-disc pl-6 my-4 space-y-1.5">{list.map((x, i) => <li key={i} dangerouslySetInnerHTML={{ __html: x.replace(/\*\*(.+?)\*\*/g, '<strong class="text-[color:var(--brand-navy)]">$1</strong>') }}/>)}</ul>); list = []; } };
    lines.forEach((ln, i) => {
      const t = ln.trim();
      if (t.startsWith("### ")) { flush(); out.push(<h3 key={i} className="font-display text-xl md:text-2xl font-bold mt-8 mb-3 text-[color:var(--brand-navy)]">{t.slice(4)}</h3>); }
      else if (t.startsWith("## ")) { flush(); out.push(<h2 key={i} className="font-display text-2xl md:text-3xl font-bold mt-10 mb-4 text-[color:var(--brand-navy)]">{t.slice(3)}</h2>); }
      else if (/^[-*]\s+/.test(t)) { list.push(t.replace(/^[-*]\s+/, "")); }
      else if (t === "") { flush(); }
      else { flush(); out.push(<p key={i} className="my-4 leading-relaxed" dangerouslySetInnerHTML={{ __html: t.replace(/\*\*(.+?)\*\*/g, '<strong class="text-[color:var(--brand-navy)]">$1</strong>') }}/>); }
    });
    flush();
    return out;
  };

  const renderContent = (content) => {
    if (isHtmlContent) {
      return (
        <div
          className="kap-article-body"
          dangerouslySetInnerHTML={{ __html: content }}
        />
      );
    }
    return renderMarkdown(content);
  };

  // Schema.org JSON-LD — Article + FAQPage for rich SERP
  const article = {
    "@context": "https://schema.org", "@type": "Article",
    "headline": p.title,
    "description": p.meta_description,
    "image": [p.og_image || p.cover_image].filter(Boolean),
    "datePublished": p.published_at || p.created_at,
    "dateModified": p.updated_at,
    "author": { "@type": "Person", "name": p.author, "description": p.author_bio || undefined },
    "publisher": { "@type": "Organization", "name": "KLOUB'AIR PLOMBERIE", "logo": { "@type": "ImageObject", "url": "https://kap-clim.fr/logo.png" } },
    "keywords": (p.tags || []).concat(p.keyword ? [p.keyword] : []).join(", "),
  };
  const faqLd = p.faq?.length ? {
    "@context": "https://schema.org", "@type": "FAQPage",
    "mainEntity": p.faq.map(f => ({
      "@type": "Question", "name": f.question,
      "acceptedAnswer": { "@type": "Answer", "text": f.answer }
    })),
  } : null;

  const publishDate = p.published_at || p.created_at;
  const readingTime = p.reading_time || Math.max(1, Math.round((p.content || "").split(/\s+/).length / 200));

  return (
    <>
      <title>{p.meta_title || p.title}</title>
      <meta name="description" content={p.meta_description} />
      {p.og_image && <meta property="og:image" content={p.og_image} />}
      {p.cover_image && !p.og_image && <meta property="og:image" content={p.cover_image} />}
      <meta property="og:type" content="article" />
      <meta property="og:title" content={p.meta_title || p.title} />
      <meta property="og:description" content={p.meta_description || p.excerpt} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(article) }} />
      {faqLd && <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqLd) }} />}

      <article className="py-10 md:py-14">
        <div className="max-w-3xl mx-auto px-4 md:px-8">
          <nav className="text-xs text-[hsl(var(--muted-foreground))] mb-4 flex items-center gap-2 flex-wrap">
            <Link to="/">Accueil</Link><ChevronRight className="w-3 h-3" />
            <Link to="/blog">Blog</Link><ChevronRight className="w-3 h-3" />
            <span>{CATS[p.category] || p.category}</span>
          </nav>
          <div className="text-[11px] font-bold uppercase tracking-wider text-[color:var(--brand-sky)] mb-3">{CATS[p.category] || p.category}</div>
          <h1 className="font-display text-3xl md:text-5xl font-black text-[color:var(--brand-navy)] leading-[1.05]">{p.title}</h1>
          {p.excerpt && <p className="mt-5 text-lg md:text-xl text-[hsl(var(--foreground))]/75 leading-relaxed font-medium">{p.excerpt}</p>}

          <div className="mt-5 flex items-center gap-4 text-xs text-[hsl(var(--muted-foreground))] flex-wrap border-y border-[hsl(var(--border))] py-3.5">
            <span className="flex items-center gap-1.5"><User className="w-3.5 h-3.5" /> {p.author}</span>
            <span className="flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5" /> {new Date(publishDate).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })}</span>
            <span className="flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> {readingTime} min de lecture</span>
          </div>

          {p.cover_image && <img src={p.cover_image} alt={p.cover_alt || p.title} className="mt-7 rounded-2xl w-full aspect-video object-cover shadow-xl" />}

          <div className="mt-8 text-base md:text-lg text-[hsl(var(--foreground))] font-body prose-lg max-w-none">
            {renderContent(p.content)}
          </div>

          {/* Gallery */}
          {p.gallery?.length > 0 && (
            <div className="mt-10 grid md:grid-cols-2 gap-3">
              {p.gallery.map((g, i) => <img key={i} src={g} alt={`${p.title} ${i+1}`} className="rounded-2xl w-full aspect-[4/3] object-cover shadow-md" />)}
            </div>
          )}

          {/* FAQ — Rich snippet trigger */}
          {p.faq?.length > 0 && (
            <section className="mt-12 rounded-3xl bg-gradient-to-br from-[color:var(--brand-sky)]/5 to-white border border-[hsl(var(--border))] p-6 md:p-8">
              <h2 className="font-display text-2xl md:text-3xl font-black text-[color:var(--brand-navy)] mb-5">Questions fréquentes</h2>
              <div className="space-y-2.5">
                {p.faq.map((f, i) => (
                  <details key={i} className="group rounded-xl bg-white border border-[hsl(var(--border))] p-4 open:shadow-md">
                    <summary className="cursor-pointer font-display font-bold text-[color:var(--brand-navy)] flex items-start gap-3 list-none">
                      <CheckCircle2 className="w-4 h-4 mt-0.5 text-[color:var(--brand-sky)] flex-shrink-0" />
                      <span className="flex-1">{f.question}</span>
                    </summary>
                    <div className="mt-3 pl-7 text-sm text-[hsl(var(--foreground))]/80 leading-relaxed">{f.answer}</div>
                  </details>
                ))}
              </div>
            </section>
          )}

          {/* Tags + maillage interne */}
          {(p.tags?.length > 0 || p.related_services?.length > 0 || p.related_cities?.length > 0) && (
            <div className="mt-10 space-y-4">
              {p.tags?.length > 0 && (
                <div className="flex items-center gap-2 flex-wrap">
                  <Tag className="w-3.5 h-3.5 text-[hsl(var(--muted-foreground))]" />
                  {p.tags.map((t, i) => <span key={i} className="px-3 py-1 rounded-full bg-[hsl(var(--muted))] text-xs font-semibold text-[color:var(--brand-navy)]">#{t}</span>)}
                </div>
              )}
              {p.related_services?.length > 0 && (
                <div className="flex items-center gap-2 flex-wrap text-sm">
                  <strong className="text-[color:var(--brand-navy)]">Services liés :</strong>
                  {p.related_services.map((s, i) => <Link key={i} to={`/services/${s}`} className="text-[color:var(--brand-sky)] hover:underline font-semibold">{s}</Link>)}
                </div>
              )}
              {p.related_cities?.length > 0 && (
                <div className="flex items-center gap-2 flex-wrap text-sm">
                  <MapPin className="w-3.5 h-3.5 text-[color:var(--brand-sky)]" />
                  <strong className="text-[color:var(--brand-navy)]">Villes :</strong>
                  {p.related_cities.map((c, i) => <span key={i} className="font-semibold text-[color:var(--brand-navy)]">{c}{i < p.related_cities.length - 1 ? "," : ""}</span>)}
                </div>
              )}
            </div>
          )}

          {/* Author bio */}
          {p.author_bio && (
            <div className="mt-10 rounded-2xl bg-[hsl(var(--muted))]/40 p-5 flex items-start gap-4">
              <div className="w-12 h-12 rounded-full bg-[color:var(--brand-navy)] text-white flex items-center justify-center font-bold text-lg flex-shrink-0">
                {p.author.charAt(0)}
              </div>
              <div>
                <div className="font-display font-bold text-[color:var(--brand-navy)]">{p.author}</div>
                <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">{p.author_bio}</p>
              </div>
            </div>
          )}

          {/* Custom CTA — destination can be an internal path or an external URL */}
          {(p.cta_title || p.cta_text) && (() => {
            const url = p.cta_url || "/devis";
            const label = p.cta_label || "Devis gratuit sous 24h";
            const isExternal = /^https?:\/\//.test(url);
            const btn = (
              <span className="inline-flex items-center gap-2 px-6 h-12 rounded-full bg-[color:var(--brand-red)] hover:bg-[color:var(--brand-red)]/90 font-bold shadow-lg transition-all hover:-translate-y-0.5">
                {label} <ArrowRight className="w-4 h-4" />
              </span>
            );
            return (
              <div className="mt-12 rounded-3xl bg-[color:var(--brand-navy)] text-white p-8 md:p-10 text-center">
                <h3 className="font-display text-2xl md:text-3xl font-black mb-2">{p.cta_title || "Un projet en tête ?"}</h3>
                {p.cta_text && <p className="text-white/80 mb-5 max-w-xl mx-auto">{p.cta_text}</p>}
                {isExternal ? (
                  <a href={url} target="_blank" rel="noopener noreferrer" data-testid="blog-cta-external">{btn}</a>
                ) : (
                  <Link to={url} data-testid="blog-cta-internal">{btn}</Link>
                )}
              </div>
            );
          })()}
        </div>
      </article>
      <CTASection />
    </>
  );
}
